import React from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Activity, ScrollText } from 'lucide-react'
import { BulkOperationsList } from "@/components/mailbox-management/BulkOperationsList"
import { WebhookFailureMonitor } from "@/components/dashboard/WebhookFailureMonitor"
import { WebhookMonitor } from "@/components/dashboard/WebhookMonitor"
import { AutoRepliesWebhookMonitor } from '@/components/webhook-monitoring/AutoRepliesWebhookMonitor'
import { MailboxSyncMonitor } from '@/components/sync-monitoring/MailboxSyncMonitor'

export const ActivityLogsSection = () => {
  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="py-4">
          <p className="text-sm text-blue-900">
            <strong>Activity Logs:</strong> Monitor system operations, webhook health, mailbox sync status, and bulk operations. 
            Each tab below shows a different aspect of your platform's activity and health.
          </p>
        </CardContent>
      </Card>

      <Tabs defaultValue="sync-status" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="sync-status">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              <span>Mailbox Sync</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="webhook-monitor">
            <div className="flex items-center gap-2">
              <ScrollText className="h-4 w-4" />
              <span>Webhook Health</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="system-logs">
            <div className="flex items-center gap-2">
              <ScrollText className="h-4 w-4" />
              <span>Operations Log</span>
            </div>
          </TabsTrigger>
        </TabsList>

        {/* Tab 1: Mailbox Sync Status */}
        <TabsContent value="sync-status" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-green-600" />
                Mailbox Two-Way Sync Monitor
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Monitor sync between your dashboard and Smartlead. View discrepancies, connection health, and sync alerts.
              </p>
            </CardHeader>
            <CardContent>
              <MailboxSyncMonitor />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab 2: Webhook Health Monitors */}
        <TabsContent value="webhook-monitor" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ScrollText className="h-5 w-5 text-orange-600" />
                Webhook Monitoring Dashboard
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Track webhook failures, performance, and health across all integrated services.
              </p>
            </CardHeader>
          </Card>
          
          <WebhookFailureMonitor />
          <WebhookMonitor />
          <AutoRepliesWebhookMonitor />
        </TabsContent>

        {/* Tab 3: System Operations Log */}
        <TabsContent value="system-logs" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5 text-blue-600" />
                Bulk Operations History
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Track bulk operations performed on mailboxes and domains, including status updates and configuration changes.
              </p>
            </CardHeader>
            <CardContent>
              <BulkOperationsList />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ScrollText className="h-5 w-5 text-gray-600" />
                System Events
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-2">
                Application-level events, errors, and notifications.
              </p>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-gray-500">
                <ScrollText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p className="text-sm">System events logging coming soon</p>
                <p className="text-xs text-gray-400 mt-1">Error logs, sync activities, and system notifications will appear here</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}